import { useState, useEffect, useCallback, useRef } from 'react';
import { RecurringTimeEntry, TemplateType, MonthlyPattern } from '../services/types';
import { Resource } from '../services/types';
import {
  getActiveCompanies,
  CompanyOption,
  getBidJobsForCompany,
  getDefaultBidJob,
  BidJob,
  createRecurringEntry,
  updateRecurringEntry,
  deleteRecurringEntry,
  applyRecurringEntry,
  getExistingDates,
  RecurringProgressEntry,
  downloadAdjustmentSheet,
  previewAdjustmentSheet,
  applyAdjustments,
  AdjustmentPreview,
} from '../services/api';
import {
  DAY_NAMES,
  DAY_NAMES_FULL,
  NTH_LABELS,
  getFrequencyDatesForMonth,
  getFrequencySummary,
} from '../utils/recurringEntryUtils';

interface RecurringTemplateModalProps {
  isOpen: boolean;
  template: RecurringTimeEntry | null; // null = create new
  onClose: () => void;
  onSaved: () => void;
  resourceId: number;
  resources: Resource[];
  progressData?: RecurringProgressEntry;
}

type Tab = 'details' | 'calendar' | 'roundtrip';

export default function RecurringTemplateModal({
  isOpen,
  template,
  onClose,
  onSaved,
  resourceId,
  resources,
  progressData,
}: RecurringTemplateModalProps) {
  const isCreateMode = !template;
  const [activeTab, setActiveTab] = useState<Tab>('details');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formDirty, setFormDirty] = useState(false);
  const markDirty = useCallback(() => setFormDirty(true), []);

  // Template type (for create)
  const [rTemplateType, setRTemplateType] = useState<TemplateType>('client');

  // Common form fields
  const [rTemplateName, setRTemplateName] = useState('');
  const [rNotes, setRNotes] = useState('');
  const [rFrequency, setRFrequency] = useState<'daily' | 'weekly' | 'biweekly' | 'monthly'>('weekly');
  const [rDayOfWeek, setRDayOfWeek] = useState(1);
  const [rDayOfMonth, setRDayOfMonth] = useState(1);
  const [rBillable, setRBillable] = useState(true);
  const [rMonthlyPattern, setRMonthlyPattern] = useState<MonthlyPattern>('dayOfMonth');
  const [rNthOccurrence, setRNthOccurrence] = useState(1);
  const [rBiweeklyAnchor, setRBiweeklyAnchor] = useState('');

  // Client template fields
  const [rCompanies, setRCompanies] = useState<CompanyOption[]>([]);
  const [rSelectedCompany, setRSelectedCompany] = useState<CompanyOption | null>(null);
  const [rHours, setRHours] = useState('1');
  const [rCapMode, setRCapMode] = useState(false);
  const [rMonthlyHoursCap, setRMonthlyHoursCap] = useState('');

  // Activity template fields
  const [rActivityClients, setRActivityClients] = useState<Array<{
    companyId: number; companyName: string; hoursWorked: number;
    bidJobNumber?: string; bidJobDescription?: string;
  }>>([]);
  const [rAddCompany, setRAddCompany] = useState<CompanyOption | null>(null);
  const [rAddHours, setRAddHours] = useState('1');

  // Bid-job state
  const [rBidJobNumber, setRBidJobNumber] = useState<string | undefined>();
  const [rBidJobDescription, setRBidJobDescription] = useState<string | undefined>();
  const [bidJobCache, setBidJobCache] = useState<Record<number, BidJob[]>>({});

  const addCompanySelectRef = useRef<HTMLSelectElement>(null);

  // Calendar/apply state
  const [applyMonth, setApplyMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [applySelectedDates, setApplySelectedDates] = useState<Set<string>>(new Set());
  const [applyExistingDates, setApplyExistingDates] = useState<string[]>([]);
  const [applyEntryCounts, setApplyEntryCounts] = useState<Record<string, number>>({});
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyResult, setApplyResult] = useState<{ message: string; type: 'success' | 'warning' | 'error'; errors?: string[] } | null>(null);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Roundtrip / adjustment-sheet state (activity templates only)
  const [rtTargetMonth, setRtTargetMonth] = useState<string>(() => {
    const now = new Date();
    const next = new Date(now.getFullYear(), now.getMonth(), 1);
    return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
  });
  const [rtUploading, setRtUploading] = useState(false);
  const [rtPreview, setRtPreview] = useState<AdjustmentPreview | null>(null);
  const [rtError, setRtError] = useState<string | null>(null);
  const [rtApplyDate, setRtApplyDate] = useState<string>(() => {
    const now = new Date();
    return now.toISOString().slice(0, 10);
  });
  const [rtApplying, setRtApplying] = useState(false);
  const [rtApplyResult, setRtApplyResult] = useState<{ message: string; type: 'success' | 'warning' | 'error'; errors?: string[] } | null>(null);
  const rtFileRef = useRef<HTMLInputElement>(null);

  // Initialize form from template when modal opens
  useEffect(() => {
    if (!isOpen) return;

    setActiveTab('details');
    setIsEditing(false);
    setError(null);
    setFormDirty(false);
    setShowDeleteConfirm(false);
    setApplyResult(null);
    setRtPreview(null);
    setRtError(null);
    setRtApplyResult(null);

    if (template) {
      // Existing template — populate fields
      populateFormFromTemplate(template);
      // Load existing dates for calendar
      loadExistingDates(template, applyMonth.year, applyMonth.month);
    } else {
      // Create mode
      resetForm();
      setIsEditing(true);
    }
  }, [isOpen, template?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load companies when entering edit/create mode
  useEffect(() => {
    if (!isOpen || (!isCreateMode && !isEditing)) return;
    getActiveCompanies().then(setRCompanies).catch(console.error);
  }, [isOpen, isEditing, isCreateMode]);

  const populateFormFromTemplate = async (t: RecurringTimeEntry) => {
    setRTemplateType(t.templateType || 'client');
    setRTemplateName(t.templateName);
    setRNotes(t.summaryNotes);
    setRFrequency(t.frequency);
    setRDayOfWeek(t.dayOfWeek ?? 1);
    setRDayOfMonth(t.dayOfMonth ?? 1);
    setRMonthlyPattern(t.monthlyPattern ?? 'dayOfMonth');
    setRNthOccurrence(t.nthOccurrence ?? 1);
    setRBiweeklyAnchor(t.biweeklyAnchor ?? '');
    setRBillable(t.isBillable);
    if (t.templateType === 'activity' && t.clients) {
      setRActivityClients(t.clients.map(c => ({
        companyId: c.companyId,
        companyName: c.companyName,
        hoursWorked: c.hoursWorked,
        bidJobNumber: c.bidJobNumber,
        bidJobDescription: c.bidJobDescription,
      })));
      const cache: Record<number, BidJob[]> = {};
      for (const c of t.clients) {
        if (!cache[c.companyId]) {
          try { cache[c.companyId] = await getBidJobsForCompany(c.companyId); }
          catch { cache[c.companyId] = []; }
        }
      }
      setBidJobCache(cache);
    } else {
      if (t.monthlyHoursCap && t.monthlyHoursCap > 0) {
        setRCapMode(true);
        setRMonthlyHoursCap(String(t.monthlyHoursCap));
        setRHours('');
      } else {
        setRCapMode(false);
        setRMonthlyHoursCap('');
        setRHours(String(t.hoursWorked ?? 1));
      }
      setRBidJobNumber(t.bidJobNumber);
      setRBidJobDescription(t.bidJobDescription);
      if (t.companyId) {
        try {
          const bjs = await getBidJobsForCompany(t.companyId);
          setBidJobCache({ [t.companyId]: bjs });
        } catch { setBidJobCache({}); }
      }
    }
  };

  const resetForm = () => {
    setRTemplateType('client');
    setRTemplateName('');
    setRSelectedCompany(null);
    setRHours('1');
    setRCapMode(false);
    setRMonthlyHoursCap('');
    setRNotes('');
    setRFrequency('weekly');
    setRDayOfWeek(1);
    setRDayOfMonth(1);
    setRBillable(true);
    setRMonthlyPattern('dayOfMonth');
    setRNthOccurrence(1);
    setRBiweeklyAnchor('');
    setRActivityClients([]);
    setRAddCompany(null);
    setRAddHours('1');
    setRBidJobNumber(undefined);
    setRBidJobDescription(undefined);
    setBidJobCache({});
    setError(null);
    setFormDirty(false);
  };

  const loadExistingDates = async (t: RecurringTimeEntry, year: number, month: number) => {
    const ym = `${year}-${String(month + 1).padStart(2, '0')}`;
    try {
      const result = await getExistingDates(t.id, ym);
      setApplyExistingDates(result.dates);
      setApplyEntryCounts(result.entryCounts || {});
    } catch {
      setApplyExistingDates([]);
      setApplyEntryCounts({});
    }
    // Pre-check frequency dates
    const preChecked = getFrequencyDatesForMonth(t, year, month);
    setApplySelectedDates(new Set(preChecked));
  };

  const handleApplyMonthChange = async (delta: number) => {
    if (!template) return;
    const newMonth = { ...applyMonth };
    newMonth.month += delta;
    if (newMonth.month < 0) { newMonth.month = 11; newMonth.year--; }
    if (newMonth.month > 11) { newMonth.month = 0; newMonth.year++; }
    setApplyMonth(newMonth);
    setApplyResult(null);
    await loadExistingDates(template, newMonth.year, newMonth.month);
  };

  const toggleApplyDate = (dateStr: string) => {
    setApplySelectedDates(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr);
      else next.add(dateStr);
      return next;
    });
  };

  const getApplyEntryCount = (): number => {
    if (!template) return 0;
    const numDates = applySelectedDates.size;
    if (template.templateType === 'activity' && template.clients) {
      return numDates * template.clients.length;
    }
    return numDates;
  };

  const handleApply = async () => {
    if (!template || !resourceId || applySelectedDates.size === 0) return;
    setApplyLoading(true);
    setApplyResult(null);
    try {
      const resource = resources.find(r => r.autoTaskId === resourceId);
      const resourceName = resource ? `${resource.firstName} ${resource.lastName}` : '';
      const dates = Array.from(applySelectedDates).sort();
      const result = await applyRecurringEntry(template.id, dates, resourceId, resourceName);

      const { created, skipped, errors } = result;
      const sk = skipped || 0;
      const errs = errors || [];

      let message: string;
      let type: 'success' | 'warning' | 'error';

      if (created === 0 && errs.length === 0) {
        message = `All ${sk} already exist — nothing to create`;
        type = 'warning';
      } else if (errs.length > 0) {
        const parts = [`Created ${created}`];
        if (sk > 0) parts.push(`skipped ${sk}`);
        parts.push(`failed ${errs.length}`);
        message = parts.join(', ');
        type = 'error';
      } else if (sk > 0) {
        message = `Created ${created}, skipped ${sk} (already exist)`;
        type = 'success';
      } else {
        message = `Created ${created} entries`;
        type = 'success';
      }

      setApplyResult({ message, type, errors: errs.length > 0 ? errs : undefined });

      // Refresh existing dates
      await loadExistingDates(template, applyMonth.year, applyMonth.month);
      setApplySelectedDates(new Set());
      onSaved(); // refresh progress in parent
    } catch (err) {
      setApplyResult({ message: err instanceof Error ? err.message : 'Failed to apply', type: 'error' });
    } finally {
      setApplyLoading(false);
    }
  };

  const handleSave = async () => {
    if (!rTemplateName.trim()) {
      setError('Template name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (template) {
        // Update existing
        const updates: Partial<RecurringTimeEntry> = {
          templateName: rTemplateName,
          summaryNotes: rNotes,
          frequency: rFrequency,
          dayOfWeek: (rFrequency === 'weekly' || rFrequency === 'biweekly' || (rFrequency === 'monthly' && rMonthlyPattern === 'nthWeekday')) ? rDayOfWeek : undefined,
          dayOfMonth: (rFrequency === 'monthly' && rMonthlyPattern === 'dayOfMonth') ? rDayOfMonth : undefined,
          monthlyPattern: rFrequency === 'monthly' ? rMonthlyPattern : undefined,
          nthOccurrence: (rFrequency === 'monthly' && rMonthlyPattern === 'nthWeekday') ? rNthOccurrence : undefined,
          biweeklyAnchor: rFrequency === 'biweekly' ? rBiweeklyAnchor : undefined,
          isBillable: rBillable,
        };
        if (template.templateType === 'client' || !template.templateType) {
          if (rCapMode) {
            updates.monthlyHoursCap = parseFloat(rMonthlyHoursCap) || 0;
            updates.hoursWorked = undefined;
          } else {
            updates.hoursWorked = parseFloat(rHours) || 1;
            updates.monthlyHoursCap = undefined;
          }
          updates.bidJobNumber = rBidJobNumber;
          updates.bidJobDescription = rBidJobDescription;
        }
        if (template.templateType === 'activity') {
          updates.clients = rActivityClients.map(c => {
            const existing = template.clients?.find(ec => ec.companyId === c.companyId);
            return {
              companyId: c.companyId,
              companyName: c.companyName,
              hoursWorked: c.hoursWorked,
              ticketId: existing?.ticketId ?? 0,
              ticketNumber: existing?.ticketNumber ?? '',
              bidJobNumber: c.bidJobNumber,
              bidJobDescription: c.bidJobDescription,
            };
          });
        }
        await updateRecurringEntry(template.id, updates);
      } else {
        // Create new
        if (rTemplateType === 'client') {
          if (!rSelectedCompany) {
            setError('Select a company');
            setSaving(false);
            return;
          }
          await createRecurringEntry({
            templateType: 'client',
            templateName: rTemplateName,
            defaultResourceId: resourceId,
            hoursWorked: rCapMode ? undefined : (parseFloat(rHours) || 1),
            monthlyHoursCap: rCapMode ? (parseFloat(rMonthlyHoursCap) || 0) : undefined,
            summaryNotes: rNotes,
            companyId: rSelectedCompany.autoTaskId,
            companyName: rSelectedCompany.companyName,
            bidJobNumber: rBidJobNumber,
            bidJobDescription: rBidJobDescription,
            isBillable: rBillable,
            frequency: rFrequency,
            dayOfWeek: (rFrequency === 'weekly' || rFrequency === 'biweekly' || (rFrequency === 'monthly' && rMonthlyPattern === 'nthWeekday')) ? rDayOfWeek : undefined,
            dayOfMonth: (rFrequency === 'monthly' && rMonthlyPattern === 'dayOfMonth') ? rDayOfMonth : undefined,
            monthlyPattern: rFrequency === 'monthly' ? rMonthlyPattern : undefined,
            nthOccurrence: (rFrequency === 'monthly' && rMonthlyPattern === 'nthWeekday') ? rNthOccurrence : undefined,
            biweeklyAnchor: rFrequency === 'biweekly' ? rBiweeklyAnchor : undefined,
          });
        } else {
          if (rActivityClients.length === 0) {
            setError('Add at least one client');
            setSaving(false);
            return;
          }
          await createRecurringEntry({
            templateType: 'activity',
            templateName: rTemplateName,
            defaultResourceId: resourceId,
            summaryNotes: rNotes,
            isBillable: rBillable,
            frequency: rFrequency,
            dayOfWeek: (rFrequency === 'weekly' || rFrequency === 'biweekly' || (rFrequency === 'monthly' && rMonthlyPattern === 'nthWeekday')) ? rDayOfWeek : undefined,
            dayOfMonth: (rFrequency === 'monthly' && rMonthlyPattern === 'dayOfMonth') ? rDayOfMonth : undefined,
            monthlyPattern: rFrequency === 'monthly' ? rMonthlyPattern : undefined,
            nthOccurrence: (rFrequency === 'monthly' && rMonthlyPattern === 'nthWeekday') ? rNthOccurrence : undefined,
            biweeklyAnchor: rFrequency === 'biweekly' ? rBiweeklyAnchor : undefined,
            clients: rActivityClients,
          });
        }
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!template) return;
    try {
      await deleteRecurringEntry(template.id);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleAddActivityClient = async () => {
    if (!rAddCompany) return;
    if (rActivityClients.some(c => c.companyId === rAddCompany.autoTaskId)) return;

    let defaultBidJob: BidJob | null = null;
    if (!bidJobCache[rAddCompany.autoTaskId]) {
      try {
        const bjs = await getBidJobsForCompany(rAddCompany.autoTaskId);
        setBidJobCache(prev => ({ ...prev, [rAddCompany.autoTaskId]: bjs }));
        defaultBidJob = getDefaultBidJob(bjs, rAddCompany.autoTaskId, resourceId || undefined);
      } catch { /* no bid-jobs */ }
    } else {
      defaultBidJob = getDefaultBidJob(bidJobCache[rAddCompany.autoTaskId], rAddCompany.autoTaskId, resourceId || undefined);
    }

    setRActivityClients([...rActivityClients, {
      companyId: rAddCompany.autoTaskId,
      companyName: rAddCompany.companyName,
      hoursWorked: parseFloat(rAddHours) || 1,
      bidJobNumber: defaultBidJob?.bidJobNumber,
      bidJobDescription: defaultBidJob?.bidJobDescription,
    }]);
    setRAddCompany(null);
    setRAddHours('1');
    markDirty();
    setTimeout(() => addCompanySelectRef.current?.focus(), 0);
  };

  const handleCloseAttempt = () => {
    if (formDirty && (isEditing || isCreateMode)) {
      if (!confirm('You have unsaved changes. Discard them?')) return;
    }
    onClose();
  };

  if (!isOpen) return null;

  const templateType = template?.templateType || rTemplateType;

  // ─── Render helpers ────────────────────────────────────────────────────

  const renderMiniCalendar = () => {
    if (!template) return null;
    const { year, month } = applyMonth;
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];

    const cells: JSX.Element[] = [];
    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} className="w-9 h-9" />);
    }

    const isActivity = template.templateType === 'activity' && template.clients;
    const expectedPerDate = isActivity ? template.clients!.length : 1;

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const isSelected = applySelectedDates.has(dateStr);
      const existCount = applyEntryCounts[dateStr] || 0;
      const isExisting = existCount > 0;
      const isFull = existCount >= expectedPerDate;
      const isPartial = isExisting && !isFull;

      cells.push(
        <button
          key={d}
          onClick={() => toggleApplyDate(dateStr)}
          className={`w-9 h-9 rounded text-xs font-medium transition-colors relative ${
            isSelected
              ? isFull
                ? 'bg-yellow-600/40 text-yellow-300 border border-yellow-500/50'
                : isPartial
                  ? 'bg-amber-600/40 text-amber-300 border border-amber-500/50'
                  : 'bg-blue-600 text-white'
              : isFull
                ? 'bg-slate-600/50 text-slate-400 line-through'
                : isPartial
                  ? 'bg-amber-900/30 text-amber-400'
                  : 'text-slate-300 hover:bg-slate-700'
          }`}
          title={isFull ? 'Fully applied' : isPartial ? `Partial: ${existCount}/${expectedPerDate} entries` : undefined}
        >
          {d}
          {isPartial && isActivity && (
            <span className="absolute -top-1 -right-1 text-[7px] bg-amber-600 text-white rounded-full w-3.5 h-3.5 flex items-center justify-center leading-none">
              {existCount}
            </span>
          )}
        </button>
      );
    }

    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => handleApplyMonthChange(-1)} className="p-1 text-slate-400 hover:text-white transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <span className="text-sm font-medium text-white">{monthNames[month]} {year}</span>
          <button onClick={() => handleApplyMonthChange(1)} className="p-1 text-slate-400 hover:text-white transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {DAY_NAMES.map(d => (
            <div key={d} className="w-9 h-6 flex items-center justify-center text-[10px] text-slate-500 font-medium">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells}
        </div>
      </div>
    );
  };

  const renderReadOnlyDetails = () => {
    if (!template) return null;
    const t = template;
    return (
      <div className="space-y-4">
        {/* Type + Frequency header */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${
            templateType === 'client'
              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
              : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
          }`}>
            {templateType === 'client' ? 'CLIENT' : 'ACTIVITY'}
          </span>
          <span className="text-sm text-slate-400">{getFrequencySummary(t)}</span>
          {!t.isBillable && <span className="text-[11px] px-2 py-0.5 rounded bg-slate-500/20 text-slate-400 border border-slate-500/30">Non-billable</span>}
        </div>

        {/* Company / Clients */}
        {templateType === 'client' && (
          <div className="space-y-1.5">
            <div className="text-sm text-slate-500">Company</div>
            <div className="text-base text-white">{t.companyName}</div>
            {t.ticketNumber && (
              <div className="text-sm text-slate-400">Ticket: {t.ticketNumber}</div>
            )}
            {t.bidJobNumber && (
              <div className="text-sm text-slate-400">Bid-Job: {t.bidJobNumber} — {t.bidJobDescription}</div>
            )}
            <div className="text-sm text-slate-400">
              {t.monthlyHoursCap && t.monthlyHoursCap > 0
                ? `${t.monthlyHoursCap}h/mo cap`
                : `${t.hoursWorked}h per entry`}
            </div>
          </div>
        )}

        {templateType === 'activity' && t.clients && (
          <div className="space-y-1.5">
            <div className="text-sm text-slate-500">Clients ({t.clients.length})</div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {t.clients.map(c => (
                <div key={c.companyId} className="flex items-center justify-between bg-slate-700/30 rounded px-2.5 py-1.5">
                  <div>
                    <span className="text-sm text-white">{c.companyName}</span>
                    {c.bidJobNumber && <span className="text-xs text-slate-500 ml-2">{c.bidJobNumber}</span>}
                  </div>
                  <span className="text-sm text-slate-400">{c.hoursWorked}h</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {t.summaryNotes && (
          <div className="space-y-1">
            <div className="text-sm text-slate-500">Notes</div>
            <div className="text-sm text-slate-300 whitespace-pre-wrap bg-slate-700/30 rounded px-3 py-2">{t.summaryNotes}</div>
          </div>
        )}

        {/* Date-level progress */}
        {progressData && (() => {
          const now = new Date();
          const expectedDates = getFrequencyDatesForMonth(t, now.getFullYear(), now.getMonth());
          const isActivity = t.templateType === 'activity' && t.clients;
          const expectedPerDate = isActivity ? t.clients!.length : 1;
          const expectedEntries = expectedDates.length * expectedPerDate;
          const applied = progressData.applied;
          const allApplied = applied >= expectedEntries;

          return expectedDates.length > 0 ? (
            <div className="space-y-2.5">
              {/* Summary line */}
              <div className="flex items-center gap-2">
                <div className="text-sm text-slate-500">This month</div>
                <span className={`text-sm font-medium px-2.5 py-0.5 rounded ${
                  allApplied ? 'bg-green-500/20 text-green-400' :
                  applied > 0 ? 'bg-amber-500/20 text-amber-400' :
                  'bg-slate-500/20 text-slate-400'
                }`}>
                  {applied}/{expectedEntries} applied
                </span>
              </div>
              {/* Date breakdown — use 3 cols for wider layout */}
              <div className="grid grid-cols-3 gap-x-4 gap-y-1">
                {expectedDates.map(d => {
                  const count = progressData.entryCounts[d] || 0;
                  const isFull = count >= expectedPerDate;
                  const isPartial = count > 0 && !isFull;
                  const dayNum = parseInt(d.split('-')[2]);
                  const dow = new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' });
                  return (
                    <div key={d} className="flex items-center gap-1.5 text-sm">
                      {isFull ? (
                        <svg className="w-4 h-4 text-green-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                      ) : (
                        <svg className="w-4 h-4 text-red-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                      )}
                      <span className={isFull ? 'text-slate-500' : 'text-slate-300'}>{dow} {dayNum}</span>
                      {isActivity && isPartial && (
                        <span className="text-amber-400 text-xs">{count}/{expectedPerDate}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Apply result feedback */}
              {applyResult && (
                <div className={`text-sm ${
                  applyResult.type === 'success' ? 'text-green-400'
                    : applyResult.type === 'warning' ? 'text-yellow-400'
                      : 'text-red-400'
                }`}>
                  {applyResult.message}
                  {applyResult.errors && applyResult.errors.length > 0 && (
                    <div className="text-xs text-red-400/80 mt-1 max-h-20 overflow-y-auto space-y-0.5">
                      {applyResult.errors.map((e, i) => (<div key={i}>{e}</div>))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null;
        })()}

        {/* Action buttons */}
        <div className="flex gap-2 pt-3 border-t border-slate-700">
          <button
            onClick={() => { setIsEditing(true); setFormDirty(false); }}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            Edit
          </button>
          {(() => {
            if (!progressData || !template) {
              return (
                <button
                  onClick={() => setActiveTab('calendar')}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600/20 text-green-400 border border-green-500/30 hover:bg-green-600/30 transition-colors"
                >
                  Apply Entries
                </button>
              );
            }
            const now = new Date();
            const expectedDates = getFrequencyDatesForMonth(template, now.getFullYear(), now.getMonth());
            const isActivity = template.templateType === 'activity' && template.clients;
            const expectedPerDate = isActivity ? template.clients!.length : 1;
            const missingDates = expectedDates.filter(d => (progressData.entryCounts[d] || 0) < expectedPerDate);
            const allApplied = missingDates.length === 0 && expectedDates.length > 0;

            if (allApplied) {
              return (
                <span className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600/20 text-green-400 border border-green-500/30 opacity-60 cursor-default">
                  All Applied
                </span>
              );
            }
            return (
              <>
                <button
                  onClick={async () => {
                    if (!template || !resourceId || missingDates.length === 0) return;
                    setApplyLoading(true);
                    setApplyResult(null);
                    try {
                      const resource = resources.find(r => r.autoTaskId === resourceId);
                      const resourceName = resource ? `${resource.firstName} ${resource.lastName}` : '';
                      const result = await applyRecurringEntry(template.id, missingDates, resourceId, resourceName);
                      const { created, skipped, errors } = result;
                      const sk = skipped || 0;
                      const errs = errors || [];
                      let message: string;
                      let type: 'success' | 'warning' | 'error';
                      if (created === 0 && errs.length === 0) {
                        message = `All ${sk} already exist — nothing to create`;
                        type = 'warning';
                      } else if (errs.length > 0) {
                        const parts = [`Created ${created}`];
                        if (sk > 0) parts.push(`skipped ${sk}`);
                        parts.push(`failed ${errs.length}`);
                        message = parts.join(', ');
                        type = 'error';
                      } else if (sk > 0) {
                        message = `Created ${created}, skipped ${sk} (already exist)`;
                        type = 'success';
                      } else {
                        message = `Created ${created} entries`;
                        type = 'success';
                      }
                      setApplyResult({ message, type, errors: errs.length > 0 ? errs : undefined });
                      onSaved();
                    } catch (err) {
                      setApplyResult({ message: err instanceof Error ? err.message : 'Failed to apply', type: 'error' });
                    } finally {
                      setApplyLoading(false);
                    }
                  }}
                  disabled={applyLoading}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {applyLoading ? 'Applying...' : `Apply Missing (${missingDates.length})`}
                </button>
                <button
                  onClick={() => setActiveTab('calendar')}
                  className="px-3 py-2 text-sm font-medium rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                  title="Pick specific dates"
                >
                  Calendar
                </button>
              </>
            );
          })()}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="ml-auto px-4 py-2 text-sm font-medium rounded-lg bg-red-600/20 text-red-400 border border-red-500/30 hover:bg-red-600/30 transition-colors"
          >
            Delete
          </button>
        </div>

        {/* Delete confirmation */}
        {showDeleteConfirm && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 space-y-2">
            <p className="text-sm text-red-300">Delete this template? This cannot be undone.</p>
            <div className="flex gap-2">
              <button onClick={handleDelete} className="px-4 py-1.5 text-sm font-medium rounded bg-red-600 text-white hover:bg-red-700">
                Yes, Delete
              </button>
              <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-1.5 text-sm font-medium rounded bg-slate-600 text-white hover:bg-slate-500">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderEditForm = () => {
    const currentType = template?.templateType || rTemplateType;
    return (
      <div className="space-y-3">
        {/* Template Type (create only) */}
        {isCreateMode && (
          <div className="flex gap-2">
            <button
              onClick={() => setRTemplateType('client')}
              className={`flex-1 p-2 rounded-lg border text-left transition-colors ${
                rTemplateType === 'client'
                  ? 'bg-blue-600/20 border-blue-500/50 text-white'
                  : 'bg-slate-700/50 border-slate-600/50 text-slate-400 hover:border-slate-500'
              }`}
            >
              <div className="text-xs font-medium">Client Template</div>
              <div className="text-[10px] mt-0.5 opacity-70">Same task for one client on a schedule</div>
            </button>
            <button
              onClick={() => setRTemplateType('activity')}
              className={`flex-1 p-2 rounded-lg border text-left transition-colors ${
                rTemplateType === 'activity'
                  ? 'bg-purple-600/20 border-purple-500/50 text-white'
                  : 'bg-slate-700/50 border-slate-600/50 text-slate-400 hover:border-slate-500'
              }`}
            >
              <div className="text-xs font-medium">Activity Template</div>
              <div className="text-[10px] mt-0.5 opacity-70">Same task for multiple clients</div>
            </button>
          </div>
        )}

        {/* Template Name */}
        <input
          type="text"
          placeholder="Template name (required)"
          value={rTemplateName}
          onChange={e => { setRTemplateName(e.target.value); markDirty(); }}
          className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
        />

        {/* Client Template: Company + Hours + Bid-Job */}
        {currentType === 'client' && (
          <>
            {isCreateMode && (
              <select
                value={rSelectedCompany?.autoTaskId || ''}
                onChange={async e => {
                  const c = rCompanies.find(c => c.autoTaskId === Number(e.target.value));
                  setRSelectedCompany(c || null);
                  markDirty();
                  if (c) {
                    try {
                      const bjs = await getBidJobsForCompany(c.autoTaskId);
                      setBidJobCache(prev => ({ ...prev, [c.autoTaskId]: bjs }));
                      const def = getDefaultBidJob(bjs, c.autoTaskId, resourceId || undefined);
                      setRBidJobNumber(def?.bidJobNumber);
                      setRBidJobDescription(def?.bidJobDescription);
                    } catch {
                      setRBidJobNumber(undefined);
                      setRBidJobDescription(undefined);
                    }
                  } else {
                    setRBidJobNumber(undefined);
                    setRBidJobDescription(undefined);
                  }
                }}
                className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Select company...</option>
                {rCompanies.map(c => (
                  <option key={c.autoTaskId} value={c.autoTaskId}>{c.companyName}</option>
                ))}
              </select>
            )}
            <div className="flex gap-2 items-end">
              <div className={rCapMode ? 'w-32' : 'w-24'}>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <label className="text-[10px] text-slate-400">{rCapMode ? 'Monthly Cap' : 'Hours'}</label>
                  <button
                    type="button"
                    onClick={() => { setRCapMode(!rCapMode); markDirty(); }}
                    className={`text-[10px] px-1.5 py-0.5 rounded font-medium transition-colors ${
                      rCapMode
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                        : 'bg-blue-500/15 text-blue-400 border border-blue-500/30 hover:bg-blue-500/25'
                    }`}
                    title={rCapMode ? 'Switch to fixed hours per entry' : 'Switch to monthly hours cap'}
                  >
                    {rCapMode ? 'Cap On' : 'Cap'}
                  </button>
                </div>
                {rCapMode ? (
                  <input type="number" step="1" min="1" value={rMonthlyHoursCap}
                    onChange={e => { setRMonthlyHoursCap(e.target.value); markDirty(); }}
                    placeholder="e.g. 40"
                    className="w-full bg-slate-700 border border-emerald-600/50 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                ) : (
                  <input type="number" step="0.25" min="0.25" value={rHours}
                    onChange={e => { setRHours(e.target.value); markDirty(); }}
                    className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                )}
              </div>
              {(() => {
                const companyId = template?.companyId ?? rSelectedCompany?.autoTaskId;
                const bjs = companyId ? bidJobCache[companyId] : undefined;
                if (!bjs || bjs.length === 0) return null;
                return (
                  <div className="flex-1">
                    <label className="text-[10px] text-slate-400 block mb-0.5">Bid-Job</label>
                    <select
                      value={rBidJobNumber || ''}
                      onChange={e => {
                        const bj = bjs.find(b => b.bidJobNumber === e.target.value);
                        setRBidJobNumber(bj?.bidJobNumber);
                        setRBidJobDescription(bj?.bidJobDescription);
                        markDirty();
                      }}
                      className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">None</option>
                      {bjs.map(bj => (
                        <option key={bj.id} value={bj.bidJobNumber}>{bj.bidJobNumber} - {bj.bidJobDescription}</option>
                      ))}
                    </select>
                  </div>
                );
              })()}
            </div>
            {rCapMode && rMonthlyHoursCap && rFrequency === 'weekly' && (
              <p className="text-[10px] text-emerald-400/80 mt-0.5">
                {rMonthlyHoursCap}h/month divided evenly across all {DAY_NAMES_FULL[rDayOfWeek]}s
              </p>
            )}
            {rCapMode && rFrequency !== 'weekly' && (
              <p className="text-[10px] text-amber-400/80 mt-0.5">
                Monthly cap requires Weekly frequency
              </p>
            )}
          </>
        )}

        {/* Activity Template: Client List */}
        {currentType === 'activity' && (
          <div className="space-y-2">
            <label className="text-[10px] text-slate-400 block">Clients</label>
            {rActivityClients.length > 0 && (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {rActivityClients.map(c => {
                  const bjs = bidJobCache[c.companyId] || [];
                  return (
                    <div key={c.companyId} className="bg-slate-700/50 rounded px-2 py-1.5 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-white flex-1 truncate">{c.companyName}</span>
                        <input type="number" step="0.25" min="0.25" value={c.hoursWorked}
                          onChange={e => {
                            setRActivityClients(prev => prev.map(ac =>
                              ac.companyId === c.companyId ? { ...ac, hoursWorked: parseFloat(e.target.value) || 1 } : ac
                            ));
                            markDirty();
                          }}
                          className="w-16 bg-slate-600 border border-slate-500 rounded px-1.5 py-0.5 text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <span className="text-[10px] text-slate-500">hrs</span>
                        <button onClick={() => { setRActivityClients(rActivityClients.filter(ac => ac.companyId !== c.companyId)); markDirty(); }}
                          className="text-slate-400 hover:text-red-400 transition-colors">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                      {bjs.length > 0 && (
                        <select value={c.bidJobNumber || ''}
                          onChange={e => {
                            const bj = bjs.find(b => b.bidJobNumber === e.target.value);
                            setRActivityClients(prev => prev.map(ac =>
                              ac.companyId === c.companyId
                                ? { ...ac, bidJobNumber: bj?.bidJobNumber, bidJobDescription: bj?.bidJobDescription }
                                : ac
                            ));
                            markDirty();
                          }}
                          className="w-full bg-slate-600 border border-slate-500 rounded px-1.5 py-0.5 text-white text-[10px] focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">No bid-job</option>
                          {bjs.map(bj => (
                            <option key={bj.id} value={bj.bidJobNumber}>{bj.bidJobNumber} - {bj.bidJobDescription}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <select ref={addCompanySelectRef} value={rAddCompany?.autoTaskId || ''}
                  onChange={e => {
                    const c = rCompanies.find(c => c.autoTaskId === Number(e.target.value));
                    setRAddCompany(c || null);
                  }}
                  className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Add company...</option>
                  {rCompanies
                    .filter(c => !rActivityClients.some(ac => ac.companyId === c.autoTaskId))
                    .map(c => (
                      <option key={c.autoTaskId} value={c.autoTaskId}>{c.companyName}</option>
                    ))}
                </select>
              </div>
              <input type="number" step="0.25" min="0.25" value={rAddHours}
                onChange={e => setRAddHours(e.target.value)}
                className="w-16 bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="hrs"
              />
              <button onClick={handleAddActivityClient} disabled={!rAddCompany}
                className="px-2 py-1.5 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
                Add
              </button>
            </div>
          </div>
        )}

        {/* Frequency row */}
        <div className="flex flex-wrap gap-2">
          <div className="flex-1 min-w-[120px]">
            <label className="text-[10px] text-slate-400 block mb-0.5">Frequency</label>
            <select value={rFrequency}
              onChange={e => {
                const val = e.target.value as 'daily' | 'weekly' | 'biweekly' | 'monthly';
                setRFrequency(val);
                markDirty();
                if (val === 'biweekly' && !rBiweeklyAnchor) {
                  const now = new Date();
                  const target = rDayOfWeek;
                  const daysUntil = (target - now.getDay() + 7) % 7 || 7;
                  const next = new Date(now);
                  next.setDate(now.getDate() + daysUntil);
                  setRBiweeklyAnchor(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`);
                }
              }}
              className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="daily">Daily (Mon-Fri)</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          {(rFrequency === 'weekly' || rFrequency === 'biweekly') && (
            <div>
              <label className="text-[10px] text-slate-400 block mb-0.5">Day</label>
              <select value={rDayOfWeek}
                onChange={e => {
                  const dow = Number(e.target.value);
                  setRDayOfWeek(dow);
                  markDirty();
                  if (rFrequency === 'biweekly') {
                    const now = new Date();
                    const daysUntil = (dow - now.getDay() + 7) % 7 || 7;
                    const next = new Date(now);
                    next.setDate(now.getDate() + daysUntil);
                    setRBiweeklyAnchor(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`);
                  }
                }}
                className="bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {DAY_NAMES.map((d, i) => (<option key={i} value={i}>{d}</option>))}
              </select>
            </div>
          )}
          {rFrequency === 'monthly' && (
            <>
              <div>
                <label className="text-[10px] text-slate-400 block mb-0.5">Pattern</label>
                <select value={rMonthlyPattern}
                  onChange={e => { setRMonthlyPattern(e.target.value as MonthlyPattern); markDirty(); }}
                  className="bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="dayOfMonth">Specific Day</option>
                  <option value="nthWeekday">Nth Weekday</option>
                </select>
              </div>
              {rMonthlyPattern === 'dayOfMonth' && (
                <div>
                  <label className="text-[10px] text-slate-400 block mb-0.5">Day</label>
                  <input type="number" min="1" max="31" value={rDayOfMonth}
                    onChange={e => { setRDayOfMonth(Number(e.target.value)); markDirty(); }}
                    className="w-16 bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              )}
              {rMonthlyPattern === 'nthWeekday' && (
                <>
                  <div>
                    <label className="text-[10px] text-slate-400 block mb-0.5">Which</label>
                    <select value={rNthOccurrence}
                      onChange={e => { setRNthOccurrence(Number(e.target.value)); markDirty(); }}
                      className="bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {NTH_LABELS.map((label, i) => (<option key={i} value={i + 1}>{label}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-slate-400 block mb-0.5">Day</label>
                    <select value={rDayOfWeek}
                      onChange={e => { setRDayOfWeek(Number(e.target.value)); markDirty(); }}
                      className="bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {DAY_NAMES.map((d, i) => (<option key={i} value={i}>{d}</option>))}
                    </select>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Notes */}
        <textarea placeholder="Summary notes (copied to each generated entry)" value={rNotes}
          onChange={e => { setRNotes(e.target.value); markDirty(); }}
          rows={2}
          className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
        />

        {/* Billable */}
        <label className="flex items-center gap-1.5 text-xs text-slate-400">
          <input type="checkbox" checked={rBillable}
            onChange={e => { setRBillable(e.target.checked); markDirty(); }}
            className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-700 text-blue-500"
          />
          Billable
        </label>

        {error && <p className="text-xs text-red-400">{error}</p>}

        {/* Save/Cancel */}
        <div className="flex gap-2">
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {saving ? 'Saving...' : isCreateMode ? 'Create' : 'Update'}
          </button>
          {!isCreateMode ? (
            <button onClick={() => { if (formDirty && !confirm('Discard changes?')) return; setIsEditing(false); populateFormFromTemplate(template!); setFormDirty(false); }}
              className="px-4 py-1.5 text-xs font-medium rounded-lg bg-slate-600 text-white hover:bg-slate-500 transition-colors">
              Cancel Edit
            </button>
          ) : (
            <button onClick={handleCloseAttempt}
              className="px-4 py-1.5 text-xs font-medium rounded-lg bg-slate-600 text-white hover:bg-slate-500 transition-colors">
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderCalendarTab = () => {
    if (!template) return null;
    return (
      <div className="space-y-3">
        {renderMiniCalendar()}

        <div className="pt-3 border-t border-slate-700 space-y-1">
          <div className="text-xs text-slate-400">
            Selected: <span className="text-white font-medium">{applySelectedDates.size} days</span>
          </div>
          {applySelectedDates.size > 0 && (
            <div className="text-xs text-slate-400">
              Entries to create: <span className="text-white font-medium">{getApplyEntryCount()}</span>
              {template.templateType === 'activity' && template.clients && (
                <span className="text-slate-500"> ({applySelectedDates.size} days x {template.clients.length} clients)</span>
              )}
            </div>
          )}
          {template.monthlyHoursCap && template.monthlyHoursCap > 0 && template.dayOfWeek !== undefined && (() => {
            const daysInMonth = new Date(applyMonth.year, applyMonth.month + 1, 0).getDate();
            let weekdayCount = 0;
            for (let d = 1; d <= daysInMonth; d++) {
              if (new Date(applyMonth.year, applyMonth.month, d).getDay() === template.dayOfWeek!) weekdayCount++;
            }
            const hoursEach = Math.round((template.monthlyHoursCap! / weekdayCount) * 100) / 100;
            return (
              <div className="text-xs text-emerald-400">
                Cap: {template.monthlyHoursCap}h / {weekdayCount} {DAY_NAMES_FULL[template.dayOfWeek!]}s = <span className="font-medium">{hoursEach}h each</span>
              </div>
            );
          })()}
          {applyExistingDates.length > 0 && (() => {
            const isAct = template.templateType === 'activity' && template.clients;
            const expected = isAct ? template.clients!.length : 1;
            const fullDays: number[] = [];
            const partialDays: { day: number; count: number }[] = [];
            for (const d of applyExistingDates) {
              const day = parseInt(d.split('-')[2]);
              const count = applyEntryCounts[d] || 0;
              if (count >= expected) fullDays.push(day);
              else partialDays.push({ day, count });
            }
            fullDays.sort((a, b) => a - b);
            partialDays.sort((a, b) => a.day - b.day);

            return (
              <div className="text-xs space-y-0.5">
                {fullDays.length > 0 && (
                  <div className="text-slate-400">Fully applied: {fullDays.join(', ')}</div>
                )}
                {partialDays.length > 0 && isAct && (
                  <div className="text-amber-400">Partial: {partialDays.map(p => `${p.day} (${p.count}/${expected})`).join(', ')}</div>
                )}
                {partialDays.length > 0 && !isAct && (
                  <div className="text-yellow-400">Already applied: {partialDays.map(p => p.day).join(', ')}</div>
                )}
              </div>
            );
          })()}
        </div>

        {applyResult && (
          <div className="space-y-1">
            <div className={`text-xs ${
              applyResult.type === 'success' ? 'text-green-400'
                : applyResult.type === 'warning' ? 'text-yellow-400'
                  : 'text-red-400'
            }`}>{applyResult.message}</div>
            {applyResult.errors && applyResult.errors.length > 0 && (
              <div className="text-[10px] text-red-400/80 max-h-20 overflow-y-auto space-y-0.5">
                {applyResult.errors.map((e, i) => (<div key={i}>{e}</div>))}
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={() => setActiveTab('details')}
            className="flex-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-600 text-white hover:bg-slate-500 transition-colors">
            Back
          </button>
          <button onClick={handleApply} disabled={applyLoading || applySelectedDates.size === 0}
            className="flex-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {applyLoading ? 'Applying...' : `Apply (${getApplyEntryCount()})`}
          </button>
        </div>
      </div>
    );
  };

  // ─── Roundtrip tab (activity templates only) ───────────────────────────

  const handleDownloadSheet = async () => {
    if (!template) return;
    setRtError(null);
    try {
      await downloadAdjustmentSheet(template.id, rtTargetMonth, resourceId || undefined);
    } catch (err) {
      setRtError(err instanceof Error ? err.message : 'Failed to download sheet');
    }
  };

  const handleUploadSheet = async (file: File) => {
    if (!template) return;
    setRtUploading(true);
    setRtError(null);
    setRtApplyResult(null);
    try {
      const preview = await previewAdjustmentSheet(template.id, file, rtTargetMonth);
      setRtPreview(preview);
      // Default the apply date to the 1st of the target month
      setRtApplyDate(`${preview.targetMonth}-01`);
    } catch (err) {
      setRtError(err instanceof Error ? err.message : 'Failed to parse sheet');
      setRtPreview(null);
    } finally {
      setRtUploading(false);
      if (rtFileRef.current) rtFileRef.current.value = '';
    }
  };

  const handleApplyAdjustments = async () => {
    if (!template || !rtPreview) return;
    setRtApplying(true);
    setRtApplyResult(null);
    try {
      const adjustments = rtPreview.clients
        .filter(c => c.status === 'ok' && c.newHours > 0)
        .map(c => ({
          ticketId: c.ticketId,
          ticketNumber: c.ticketNumber,
          companyName: c.companyName,
          hoursWorked: c.newHours,
          summaryNotes: c.newNotes && c.newNotes.length > 0 ? c.newNotes : undefined,
        }));
      if (adjustments.length === 0) {
        setRtApplyResult({ message: 'Nothing to apply — no rows with hours > 0.', type: 'warning' });
        setRtApplying(false);
        return;
      }
      const me = resources.find(r => r.autoTaskId === resourceId);
      const resourceName = me ? `${me.firstName} ${me.lastName}`.trim() : '';
      const result = await applyAdjustments(
        template.id,
        [rtApplyDate],
        resourceId,
        resourceName,
        adjustments,
      );
      const errs = result.errors;
      const created = result.created;
      const skipped = result.skipped || 0;
      if (errs && errs.length > 0) {
        setRtApplyResult({
          message: `Applied ${created} entries, ${skipped} skipped, ${errs.length} failures`,
          type: 'warning',
          errors: errs,
        });
      } else {
        setRtApplyResult({
          message: `Applied ${created} entries${skipped > 0 ? `, ${skipped} skipped (duplicates)` : ''}.`,
          type: 'success',
        });
      }
      onSaved();
    } catch (err) {
      setRtApplyResult({
        message: err instanceof Error ? err.message : 'Failed to apply adjustments',
        type: 'error',
      });
    } finally {
      setRtApplying(false);
    }
  };

  const renderRoundtripTab = () => {
    if (!template) return null;
    if (template.templateType !== 'activity') {
      return (
        <div className="text-sm text-slate-400 p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
          The roundtrip workflow is only available for <span className="text-purple-300">activity</span> templates (multi-client).
        </div>
      );
    }

    const okClients = rtPreview ? rtPreview.clients.filter(c => c.status === 'ok') : [];
    const unknownClients = rtPreview ? rtPreview.clients.filter(c => c.status === 'unknown-client') : [];
    const removedClients = rtPreview ? rtPreview.clients.filter(c => c.status === 'removed-from-template') : [];
    const totalHours = okClients.reduce((sum, c) => sum + (c.newHours || 0), 0);

    return (
      <div className="space-y-4">
        {/* Step 1: Generate sheet */}
        <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="bg-blue-500/20 text-blue-300 text-[10px] font-bold px-1.5 py-0.5 rounded">STEP 1</span>
            <span className="text-sm font-medium text-white">Generate adjustment sheet</span>
          </div>
          <p className="text-xs text-slate-400">
            Downloads an Excel with the reference month's actuals next to blank "billing month" cells. Send it to the stakeholder for markup.
          </p>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="block text-xs text-slate-400 mb-1">Billing month</label>
              <input
                type="month"
                value={rtTargetMonth}
                onChange={e => setRtTargetMonth(e.target.value)}
                className="w-full px-2 py-1.5 text-sm bg-slate-700 text-white border border-slate-600 rounded"
              />
            </div>
            <button
              onClick={handleDownloadSheet}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              Download Excel
            </button>
          </div>
        </div>

        {/* Step 2: Upload reply */}
        <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="bg-purple-500/20 text-purple-300 text-[10px] font-bold px-1.5 py-0.5 rounded">STEP 2</span>
            <span className="text-sm font-medium text-white">Upload markup</span>
          </div>
          <p className="text-xs text-slate-400">
            When you get the filled-in sheet back, upload it here to preview the diff before applying.
          </p>
          <input
            ref={rtFileRef}
            type="file"
            accept=".xlsx"
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) void handleUploadSheet(file);
            }}
            className="block w-full text-xs text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-slate-700 file:text-white hover:file:bg-slate-600"
            disabled={rtUploading}
          />
          {rtUploading && <div className="text-xs text-slate-400">Parsing sheet…</div>}
          {rtError && <div className="text-xs text-red-400">{rtError}</div>}
        </div>

        {/* Step 3: Preview + apply */}
        {rtPreview && (
          <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 space-y-3">
            <div className="flex items-center gap-2">
              <span className="bg-emerald-500/20 text-emerald-300 text-[10px] font-bold px-1.5 py-0.5 rounded">STEP 3</span>
              <span className="text-sm font-medium text-white">Review and apply</span>
            </div>

            <div className="text-xs text-slate-400 grid grid-cols-2 gap-1">
              <div>Billing month: <span className="text-white font-medium">{rtPreview.targetMonth}</span></div>
              <div>Reference month: <span className="text-white font-medium">{rtPreview.lastCycle || '— (first run)'}</span></div>
              <div>Clients to apply: <span className="text-white font-medium">{okClients.length}</span></div>
              <div>Total hours: <span className="text-white font-medium">{totalHours.toFixed(2)}</span></div>
            </div>

            <div className="max-h-72 overflow-y-auto border border-slate-700 rounded">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-700/60 text-slate-300 sticky top-0">
                  <tr>
                    <th className="px-2 py-1 text-left">Client</th>
                    <th className="px-2 py-1 text-right">Reference</th>
                    <th className="px-2 py-1 text-right">Billing</th>
                    <th className="px-2 py-1 text-right">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {okClients.map(c => {
                    const delta = c.lastCycleHours !== null ? c.newHours - c.lastCycleHours : null;
                    const deltaColor = delta == null ? 'text-slate-500' : delta > 0 ? 'text-amber-400' : delta < 0 ? 'text-blue-400' : 'text-slate-500';
                    return (
                      <tr key={c.ticketId} className="border-t border-slate-700/50">
                        <td className="px-2 py-1 text-slate-200">
                          {c.companyName}
                          {c.notesChanged && <span className="ml-1 text-[9px] text-purple-400">(note override)</span>}
                        </td>
                        <td className="px-2 py-1 text-right text-slate-400">{c.lastCycleHours?.toFixed(2) ?? '—'}</td>
                        <td className={`px-2 py-1 text-right font-medium ${c.hoursChanged ? 'text-amber-300' : 'text-white'}`}>
                          {c.newHours.toFixed(2)}
                        </td>
                        <td className={`px-2 py-1 text-right ${deltaColor}`}>
                          {delta == null ? '—' : (delta > 0 ? '+' : '') + delta.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {(unknownClients.length > 0 || removedClients.length > 0) && (
              <div className="text-[11px] space-y-1">
                {unknownClients.length > 0 && (
                  <div className="text-yellow-400">
                    {unknownClients.length} row(s) in the sheet don't match any template client (will be skipped): {unknownClients.map(c => c.companyName || c.ticketNumber).join(', ')}
                  </div>
                )}
                {removedClients.length > 0 && (
                  <div className="text-orange-400">
                    {removedClients.length} template client(s) absent from the sheet (will not be applied): {removedClients.map(c => c.companyName).join(', ')}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-end gap-2 pt-2 border-t border-slate-700">
              <div className="flex-1">
                <label className="block text-xs text-slate-400 mb-1">Apply on date</label>
                <input
                  type="date"
                  value={rtApplyDate}
                  onChange={e => setRtApplyDate(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm bg-slate-700 text-white border border-slate-600 rounded"
                />
              </div>
              <button
                onClick={handleApplyAdjustments}
                disabled={rtApplying || okClients.length === 0}
                className="px-4 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                {rtApplying ? 'Applying…' : `Apply (${okClients.length})`}
              </button>
            </div>

            {rtApplyResult && (
              <div className="space-y-1">
                <div className={`text-xs ${
                  rtApplyResult.type === 'success' ? 'text-green-400'
                    : rtApplyResult.type === 'warning' ? 'text-yellow-400'
                      : 'text-red-400'
                }`}>{rtApplyResult.message}</div>
                {rtApplyResult.errors && rtApplyResult.errors.length > 0 && (
                  <div className="text-[10px] text-red-400/80 max-h-20 overflow-y-auto space-y-0.5">
                    {rtApplyResult.errors.map((e, i) => <div key={i}>{e}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ─── Modal shell ───────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70]" onClick={handleCloseAttempt}>
      <div className="bg-slate-800 rounded-xl border border-slate-600 w-full max-w-xl shadow-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-base font-medium text-white truncate">
              {isCreateMode ? 'New Template' : template!.templateName}
            </span>
            {!isCreateMode && (
              <span className={`text-[11px] px-2 py-0.5 rounded font-medium shrink-0 ${
                templateType === 'client'
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                  : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
              }`}>
                {templateType === 'client' ? 'CLIENT' : 'ACTIVITY'}
              </span>
            )}
          </div>
          <button onClick={handleCloseAttempt} className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white shrink-0">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Tabs (only for existing templates) */}
        {!isCreateMode && !isEditing && (() => {
          const isActivity = template?.templateType === 'activity';
          const tabs: Tab[] = isActivity ? ['details', 'calendar', 'roundtrip'] : ['details', 'calendar'];
          const labels: Record<Tab, string> = { details: 'Details', calendar: 'Calendar', roundtrip: 'Roundtrip' };
          return (
            <div className="flex border-b border-slate-700 shrink-0">
              {tabs.map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? 'text-blue-400 border-b-2 border-blue-400'
                      : 'text-slate-400 hover:text-white'
                  }`}>
                  {labels[tab]}
                </button>
              ))}
            </div>
          );
        })()}

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1">
          {isCreateMode || isEditing
            ? renderEditForm()
            : activeTab === 'details'
              ? renderReadOnlyDetails()
              : activeTab === 'calendar'
                ? renderCalendarTab()
                : renderRoundtripTab()}
        </div>
      </div>
    </div>
  );
}
